// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
import { CloneQuoteOption } from '~/components/module/quotes';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValues, schema } from './helper';
import { ICloneProduct } from '~/utils/interface/inventory';
import { cloneInventory } from '~/thunks/inventory/inventoryThunk';
import { inventoryActions } from '~/thunks/inventory/inventorySlice';
// Styles, images, icons
import styles from './CloneProductForm.module.scss';
import { icons } from '~/assets';

type Props = {
  productId: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const CloneProductForm = (props: Props) => {
  //#region Destructuring Props
  const { productId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICloneProduct>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleCloneProduct = (data: ICloneProduct) => {
    setIsLoading(true);

    const body: ICloneProduct = {
      ...data,
      inventoryId: productId,
    };

    dispatch(cloneInventory(body))
      .unwrap()
      .then(() => {
        setIsLoading(false);
        onClose && onClose();
        dispatch(inventoryActions.setRefreshInventoryList(true));
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Dialog open={true} onClose={handleCloseModal} fullWidth maxWidth={'sm'}>
      <DialogTitle className={cx('header')}>
        <Stack direction={'column'} spacing={'3px'}>
          <Typography className={cx('title')}>{t('inventory_form_title_clone')}</Typography>

          <Typography className={cx('description')}>{t('inventory_form_description_clone')}</Typography>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 container rowSpacing={4} columnSpacing={5}>
          <Grid2 size={12}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    required
                    height={40}
                    width={'100%'}
                    label={t('inventory_form_description_clone_add_on')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12} mt={2}>
            <Controller
              control={control}
              name={'isKeepAddOn'}
              render={({ field: { onChange, value } }) => {
                return (
                  <CloneQuoteOption
                    label={t('inventory_form_title_clone_add_on')}
                    isChecked={value}
                    onChange={onChange}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleCloneProduct)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default CloneProductForm;
